<script setup lang="ts">
import type { Property } from '~/core/entities/property';
import type { PaginatedPropertyFilters } from '~/core/entities/property/filters';

import { cn } from '@/utils';
import { useEvents } from '~/composables/events/useEvents';
import { MixpanelProps } from '~/core/service/mixpanel';
import { PropertyFiltersPresenter } from '~/presenters/PropertyFiltersPresenter';
import { CACHE_TIME } from '~/utils';
import { nuxtPropertiesRepository } from '~~/adapters/repositories/nuxt';
import { CONDO_DETAILS } from '~~/utils/symbols';

import { carouselItemVariant } from '~/components/StandardSlider';

const { trackCtaViewMoreClicked } = useEvents();

const condominium = inject(CONDO_DETAILS);

const key = computed(() => {
	const normalizedCondoName = normalizeString(condominium?.value?.condo.name);

	return `condominium:exclusive-properties-${normalizedCondoName}`;
});

const query = computed<
	Pick<PaginatedPropertyFilters, 'isExclusive' | 'sortBy' | 'commercialIds'>
>(() => ({
	commercialIds: condominium?.value?.exclusiveProperties
}));

const defaultExclusiveProperties = { payload: [] };
const { user } = useUserSession();

const { data: properties, status: status } = useAsyncData(
	key.value,
	async () => {
		if (!condominium?.value?.exclusiveProperties.length) {
			return defaultExclusiveProperties;
		}

		try {
			const [result, error] = await nuxtPropertiesRepository.list({
				page: 1,
				perPage: 12,
				...query.value,
				markUserWishlistsId: user.value?.id
			});

			if (error) {
				return defaultExclusiveProperties;
			}

			return {
				payload: result.properties
			};
		} catch {
			return defaultExclusiveProperties;
		}
	},
	{
		default: () => defaultExclusiveProperties,
		lazy: true,
		...getCacheAsyncDataConfig<{ payload: Property[] }>(CACHE_TIME),
		watch: [() => condominium?.value?.condo]
	}
);

const title = computed(() => {
	return `Exclusividades no ${condominium?.value?.condo.name}`;
});

const seeAllHref = computed(() =>
	PropertyFiltersPresenter.getPathFromFilters(query.value)
);

const seeMoreExclusivePropertiesHref = computed(() =>
	PropertyFiltersPresenter.getPathFromFilters({
		states: [condominium?.value?.condo.state ?? ''],
		cities: [condominium?.value?.condo.city ?? ''],
		regions: [condominium?.value?.condo.region ?? ''],
		isExclusive: true
	})
);

const isEmpty = computed(() => {
	return properties.value.payload.length === 0;
});

function handleViewMoreClicked() {
	trackCtaViewMoreClicked({
		[MixpanelProps.CTA_TEXT]: ctaText,
		[MixpanelProps.CURRENT_PAGE]: 'Detalhe do Condomínio',
		[MixpanelProps.SECTION_NAME]: 'exclusives',
		[MixpanelProps.DESTINATION_URL]: seeMoreExclusivePropertiesHref.value
	});
}

const ctaText = 'Ver todos os exclusivos';

const customCarouselItemClass =
	'basis-[90%] sm:!basis-[49%] md:!basis-[49%] lg:!basis-[100%] xl:!basis-[49%]';
</script>

<template>
	<StandardSlider
		v-if="!isEmpty"
		id="exclusive-properties"
		:items="properties.payload as Property[]"
		:view-all-href="seeAllHref"
		:title="title"
		:is-loading="status === 'pending'"
		container-class="px-0! md:py-0"
		carousel-item-class="basis-[90%] sm:basis-[49%]! md:basis-[49%]! lg:basis-[100%]! xl:basis-[49%]!"
		button-class="hidden"
		button-skeleton-class="h-0! md:h-auto!"
		property-card-variant="transparentBackgroundWithoutBorder"
		section-name="exclusives"
		title-as="h3"
		@cta-view-more-clicked="handleViewMoreClicked"
	>
		<template #header>
			<header class="flex flex-col items-start gap-1 pb-6 md:pb-7">
				<div
					class="flex w-full flex-col items-start gap-3 lg:flex-row lg:items-center"
				>
					<div class="grid size-7 place-items-center bg-primary">
						<IconMaterialSymbolsLightLockOutline class="size-4 text-white" />
					</div>
					<h2
						class="flex items-center font-matter-sq text-2xl font-normal leading-[150%] tracking-[-0.03rem] text-primary md:text-[32px] md:tracking-[-0.64px]"
					>
						{{ title }}
					</h2>
				</div>

				<p class="text-base font-normal leading-[150%] text-primary">
					Imóveis que você só encontra aqui, unidades selecionadas disponíveis
					apenas no PilarHomes.
				</p>
			</header>
		</template>

		<template #default-items>
			<CarouselItem
				v-if="!isEmpty"
				:class="
					cn(carouselItemVariant({ size: 'default' }), customCarouselItemClass)
				"
			>
				<DiscoverPropertiesCard
					class="size-full min-h-[279px] sm:w-[calc(100%-16px)] md:max-w-fit"
					button-class="py-3 px-2 md:px-4"
					:title="'Mais imóveis exclusivos'"
					:description="`Explore nosso porfólio de imóveis exclusivos em ${condominium?.condo.region}.`"
					:cta-text="ctaText"
					card-class="h-[260px]"
					:cta-destination-url="seeMoreExclusivePropertiesHref"
					@cta_clicked="handleViewMoreClicked"
				/>
			</CarouselItem>
		</template>
	</StandardSlider>
</template>
